<template>
  <er-card v-loading="loading" class="st-status-graph">
    <el-row slot="header">
      <layout-toolbar justify="start" align="center">
        <p class="card-title">
          {{ `${$t("Comn_shrimptalk")} ${$t("Comn_ocf_name")}` }}
        </p>
        <el-col :span="3">
          <er-select
            :key="pondId"
            v-model="selectStId"
            @change="handleChangeInSt"
            size="mini"
            :placeholder="$t('Comn_select_shrimptalks')"
          >
            <el-option
              v-for="st in listShrimpTalks"
              :key="st.code"
              :value="st._id"
              :label="st.title || stTitle(st.code)"
            ></el-option>
          </er-select>
        </el-col>
        <el-date-picker
          class="date-picker-wrapper"
          size="mini"
          type="date"
          :format="$commonUtils.DATE_OBJ_DATE_FORMAT_WITH_YEAR"
          v-model="dateRange[1]"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
          :clearable="false"
          @change="handleValueChange"
        ></el-date-picker>
      </layout-toolbar>
    </el-row>
    <el-row :key="$i18n.locale">
      <high-charts
        :options="chartOptions"
        constructor-type="stockChart"
      ></high-charts>
    </el-row>
  </er-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import chartMixin from "@/mixins/chartMixin";
import { stOCFGraphConfig } from "./chartOptions";
import ShrimpTalk from "@/model/shrimptalk";
import { castISOStringToUserTZ } from "@/utils/commonUtils";
export default {
  mixins: [errorHandlerMixin, chartMixin],
  data: function() {
    return {
      dataViewOptions: "Raw Data",
      dateRange: [],
      params: {
        from_date: "",
        to_date: "",
        month_of: null,
        week_of: null
      },
      chartOptions: { ...stOCFGraphConfig },
      loading: false,
      selectStId: "",
      listShrimpTalks: [],
      groupByStsId: {},
      STResponseData: []
    };
  },
  computed: {
    ...mapGetters("pondDetails", {
      getStHourlyFeedGraph: "getStHourlyFeedGraph",
      getStOCFGraphData: "getStOCFGraphData",
      getSTGraphOCFHourlyData: "getSTGraphOCFHourlyData",
      getSTGraphHourlyFGData: "getSTGraphHourlyFGData",
      getStIdStDetailsOfSelectedPondId: "getStIdStDetailsOfSelectedPondId"
    }),
    ...mapGetters("user", {
      getMomentObjectUserTZ: "getMomentObjectUserTZ",
      getUserTimeZoneOffset: "getUserTimeZoneOffset"
    }),
    getPmsCount() {
      if (this.getStIdStDetailsOfSelectedPondId[this.selectStId]) {
        return this.getStIdStDetailsOfSelectedPondId[this.selectStId]
          .pond_mothers.length;
      }
      return 0;
    },
    dataViewOptionsStr() {
      return {
        "Raw Data": "Pond_raw_data",
        "Average Data": "Comn_avrg_data"
      };
    },
    defaultMomentDtRangeForCurrCulture() {
      return [
        this.getMomentObjectUserTZ()
          .subtract(3, "days")
          .startOf("days"),
        this.getMomentObjectUserTZ()
      ];
    },
    pickerOptions() {
      return {
        disabledDate: this.disableDateMethod
      };
    },
    stTitle() {
      return code => {
        return ShrimpTalk.v1Title(code);
      };
    }
  },
  methods: {
    ...mapActions("pondDetails", {
      fetchHourWisePondFeed: "fetchHourWisePondFeed",
      fetchStStatsData: "fetchStStatsData"
    }),
    async initComponent() {
      this.loading = true;
      try {
        const shrimp_talks = this.getSelectedPond.shrimp_talks || [];
        if (shrimp_talks.length > 0) {
          this.listShrimpTalks = this.$lodash
            .cloneDeep(shrimp_talks)
            .sort((a, b) =>
              this.$commonUtils.alphaNumericComparator(a.title, b.title)
            );
          const stIds = shrimp_talks.map(st => st._id);
          if (
            (!this.selectStId && this.selectStId === "") ||
            stIds.indexOf(this.selectStId) === -1
          ) {
            this.selectStId = this.listShrimpTalks[0]._id;
          }
          await this.fetchStConfigData(this.selectStId);
          this.initAxisTextKeys("Comn_date_and_time", "Comn_feed_g");
        } else {
          this.selectStId = "";
          this.listShrimpTalks = [];
        }
        this.initChartLang();
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    },
    parseStOCFData(stOCFData, stFgData, dateRange) {
      const isoString = castISOStringToUserTZ(
        dateRange[1] + "T00:00:00.000Z",
        this.getUserTimeZoneOffset
      );
      const totalDayKeys = [];
      for (let hour = 0; hour < 24; hour++) {
        const correctDate = this.$moment(isoString).startOf("day");
        const eachHour = correctDate.add(hour, "hours").valueOf();
        totalDayKeys.push(eachHour);
      }
      this.STResponseData = totalDayKeys.map(x => {
        if (stOCFData[x]) {
          return {
            x,
            y: stOCFData[x],
            info: {
              total_ocf: stOCFData[x] * this.getPmsCount,
              pms: this.getPmsCount,
              fgi: stFgData[x]
            }
          };
        }
        return {
          x,
          y: 0,
          info: {
            total_ocf: 0,
            pms: this.getPmsCount,
            fgi: 0
          }
        };
      });
    },
    handleValueChange: function(currentSelectedValues) {
      let value = Array.isArray(currentSelectedValues)
        ? currentSelectedValues[1]
        : currentSelectedValues;
      value = this.$moment(value, "YYYY-MM-DD");
      const beforeStartOfCulture = value.isBefore(
        this.getMomentObjArrOfCultureDateRange[0],
        "days"
      );
      const afterEndOfCulture = value.isAfter(
        this.getMomentObjArrOfCultureDateRange[1],
        "days"
      );
      if (beforeStartOfCulture) {
        value = this.getMomentObjArrOfCultureDateRange[0];
      } else if (afterEndOfCulture) {
        value = this.getMomentObjArrOfCultureDateRange[1];
      }
      this.params.from_date = value.format("YYYY-MM-DD");
      this.params.to_date = value.format("YYYY-MM-DD");
      this.initComponent();
    },
    handleChangeInSt: async function(stId) {
      if (this.groupByStsId[stId]) {
        await this.fetchStConfigData(stId);
        this.initChartSeries(
          this.groupByStsId[stId] || [],
          this.getSTGraphOCFHourlyData || []
        );
      } else {
        this.initChartSeries([], []);
      }
    },
    async fetchStConfigData(stId) {
      const params = { ...this.params };
      params.from_date += "T00:00:00.000Z";
      params.to_date += "T23:59:59.999Z";
      await this.fetchStStatsData({ stId, params, graph: "ST_OCF" });
    },
    generateData() {
      const schedulePMsFeed = [];
      const automaticPMsFeed = [];
      const startDate = this.$moment(this.dateRange[0]);
      const timeGap = 1000;
      for (let i = 0; i < timeGap; i++) {
        schedulePMsFeed.push({
          x: startDate.add(i, "minutes").toDate(),
          y: Math.random() * 100
        });
        automaticPMsFeed.push({
          x: startDate.add(i, "minutes").toDate(),
          y: Math.random() * 100
        });
      }
      return { automaticPMsFeed, schedulePMsFeed };
    },
    initChart() {
      let stResponseData = [];
      if ((this.getStOCFGraphData || []).length === 0) {
        this.chartOptions.series = [];
        return;
      }
      this.parseStOCFData(
        this.getSTGraphOCFHourlyData,
        this.getSTGraphHourlyFGData,
        this.dateRange
      );
      stResponseData = this.STResponseData || [];
      this.initChartSeries(stResponseData);
    },
    initChartSeries(hourlyIntakeResponse) {
      this.chartOptions.series = [];
      if (hourlyIntakeResponse.length > 0) {
        this.chartOptions.series.push({
          name: this.$t("Comn_ocf_name"),
          type: "column",
          data: hourlyIntakeResponse,
          pointWidth: 10,
          color: "#758e32",
          dataGrouping: {
            enabled: false
          },
          visible: true
        });
      }
    }
  }
};
</script>
<style lang="scss">
.st-status-graph {
  .el-card__header .date-picker-wrapper {
    margin-left: auto;
    @include responsiveProperty(width, 110px, 110px, 140px);
  }
  .el-select .el-input__inner {
    cursor: pointer;
    margin-right: 15px;
    padding-right: 35px;
  }

  .el-input--suffix .el-input__inner {
    padding-right: 18px;
    text-align: center;
  }
}
</style>
